import { useState, useMemo, useEffect } from "react";
import { useRealtimeCases, Case, ARCHIVED_STATUSES } from "@/lib/useRealtimeCases";
import { statusColors, orderedStatuses } from "@/lib/statusColors";
import { CaseDrawer } from "@/components/kanban/CaseDrawer";
import { MultiSelectDropdown } from "@/components/ui/MultiSelectDropdown";
import {
  formatParisTime,
  getParisDateKey,
  toDateKeyFromLocalDate,
} from "@/lib/parisDateTime";

const SESSION_STORAGE_KEY = "calendar_selected_statuses";

/** Statuts cochés par défaut : tous sauf Supprimé/Rejeté */
const DEFAULT_STATUSES: string[] = orderedStatuses.filter(
  (s) => !ARCHIVED_STATUSES.includes(s)
);

export default function CalendarPage() {
  const { cases, loading, error, archivedLoading, loadArchivedCases } = useRealtimeCases();
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [selectedCenter, setSelectedCenter] = useState<string>("all");
  
  // Initialiser les statuts sélectionnés depuis le sessionStorage, sinon tous sauf Supprimé/Rejeté
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [...DEFAULT_STATUSES];
      }
    }
    return [...DEFAULT_STATUSES];
  });

  // Persister les statuts sélectionnés dans le sessionStorage
  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  // Supprimé/Rejeté ne sont pas chargés au démarrage : on les charge dès qu'ils sont cochés
  const wantsArchived = selectedStatuses.some((s) => ARCHIVED_STATUSES.includes(s));
  useEffect(() => {
    if (wantsArchived) loadArchivedCases();
  }, [wantsArchived]);

  const [currentMonthStart, setCurrentMonthStart] = useState<Date>(() => {
    // Démarrer au 1er du mois courant
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Extraire les centres uniques
  const centers = useMemo(() => {
    const uniqueCenters = Array.from(new Set(cases.map(c => c.center).filter(Boolean))) as string[];
    return uniqueCenters.sort();
  }, [cases]);

  // Calculer les jours du mois (avec padding début/fin pour grille 7 colonnes)
  const monthDates = useMemo(() => {
    const year = currentMonthStart.getFullYear();
    const month = currentMonthStart.getMonth();
    
    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = dimanche, 1 = lundi, etc.
    
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);
    const lastDate = lastDay.getDate();
    
    // Padding début (jours du mois précédent)
    const paddingStart = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Lundi = 0, Dimanche = 6
    
    // Padding fin (jours du mois suivant pour compléter la grille)
    const totalCells = paddingStart + lastDate;
    const paddingEnd = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    
    const dates: Date[] = [];
    
    // Ajouter les jours du mois précédent (grisés)
    for (let i = paddingStart - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      dates.push(date);
    }
    
    // Ajouter les jours du mois courant
    for (let i = 1; i <= lastDate; i++) {
      const date = new Date(year, month, i);
      dates.push(date);
    }
    
    // Ajouter les jours du mois suivant (grisés)
    for (let i = 1; i <= paddingEnd; i++) {
      const date = new Date(year, month + 1, i);
      dates.push(date);
    }
    
    return dates;
  }, [currentMonthStart]);

  // Filtrer et grouper les événements par date
  const eventsByDate = useMemo(() => {
    const pad2 = (value: number) => String(value).padStart(2, "0");
    const year = currentMonthStart.getFullYear();
    const month = currentMonthStart.getMonth() + 1;
    const lastDate = new Date(year, month, 0).getDate();
    const startKey = `${year}-${pad2(month)}-01`;
    const endKey = `${year}-${pad2(month)}-${pad2(lastDate)}`;

    const filtered = cases.filter(c => {
      // Filtrer par centre
      const matchesCenter = selectedCenter === "all" || c.center === selectedCenter;
      
      // Filtrer par statuts (multi-select)
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(c.status);
      
      // Filtrer par date d'événement dans le mois affiché
      if (!c.event_date) return false;

      const eventDateKey = getParisDateKey(c.event_date);
      const matchesDateRange = eventDateKey >= startKey && eventDateKey <= endKey;
      
      return matchesCenter && matchesStatus && matchesDateRange;
    });

    // Grouper par date
    const grouped: Record<string, Case[]> = {};
    filtered.forEach(c => {
      if (c.event_date) {
        const dateKey = getParisDateKey(c.event_date);
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(c);
      }
    });

    return grouped;
  }, [cases, selectedCenter, selectedStatuses, currentMonthStart]);

  const goToPreviousMonth = () => {
    setCurrentMonthStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentMonthStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonthStart(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
    });
  };

  const todayParisKey = getParisDateKey(new Date());
  const isToday = (date: Date) => {
    return toDateKeyFromLocalDate(date) === todayParisKey;
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonthStart.getMonth();
  };

  const getMonthYearLabel = () => {
    return currentMonthStart.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Chargement du calendrier...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">Erreur : {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Calendrier des événements
              </h1>
              <p className="text-sm text-gray-500 mt-1 capitalize">
                {getMonthYearLabel()}
              </p>
            </div>

            {/* Navigation période */}
            <div className="flex gap-2">
              <button
                onClick={goToPreviousMonth}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Mois précédent
              </button>
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-medium"
              >
                Aujourd'hui
              </button>
              <button
                onClick={goToNextMonth}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Mois suivant →
              </button>
            </div>
          </div>

          {/* Filtres */}
          <div className="flex gap-4 items-start">
            {/* Filtre par centre */}
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setSelectedCenter("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCenter === "all"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Tous les centres
              </button>
              {centers.map((center) => (
                <button
                  key={center}
                  onClick={() => setSelectedCenter(center)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCenter === center
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {center}
                </button>
              ))}
            </div>

            {/* Filtre par statuts (multi-select) */}
            <div className="w-80">
              <MultiSelectDropdown
                label="Statuts"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                options={[...orderedStatuses]}
                placeholder="Sélectionner des statuts..."
                allowCustom={false}
              />
              {archivedLoading && (
                <p className="text-xs text-gray-500 mt-1">Chargement des dossiers supprimés/rejetés…</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Calendrier */}
      <div className="p-6">
        {/* En-têtes des jours de la semaine */}
        <div className="grid grid-cols-7 gap-4 mb-4">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>

        {/* Grille des jours */}
        <div className="grid grid-cols-7 gap-4">
          {monthDates.map((date, index) => {
            const dateKey = toDateKeyFromLocalDate(date);
            const dayEvents = eventsByDate[dateKey] || [];
            const today = isToday(date);
            const currentMonth = isCurrentMonth(date);

            return (
              <div
                key={index}
                className={`bg-white rounded-lg shadow-sm border-2 min-h-[150px] ${
                  today ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200"
                } ${!currentMonth ? "opacity-40" : ""}`}
              >
                {/* En-tête du jour */}
                <div className={`p-2 border-b ${today ? "bg-indigo-50" : "bg-gray-50"}`}>
                  <div className={`text-sm font-semibold ${today ? "text-indigo-900" : "text-gray-900"}`}>
                    {formatDate(date)}
                  </div>
                  {dayEvents.length > 0 && (
                    <div className="text-xs text-gray-600 mt-0.5">
                      {dayEvents.length} événement{dayEvents.length > 1 ? "s" : ""}
                    </div>
                  )}
                </div>

                {/* Liste des événements */}
                <div className="p-1 space-y-1 overflow-y-auto max-h-[200px]">
                  {dayEvents.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => setSelectedCase(event)}
                      className="w-full text-left p-2 rounded-md hover:opacity-90 transition-all border-2"
                      style={{
                        backgroundColor: statusColors[event.status] || "#F3F3F3",
                        borderColor: statusColors[event.status] || "#E5E7EB",
                      }}
                    >
                      {/* Nom de l'entreprise */}
                      <div className="text-xs font-semibold text-gray-900 truncate mb-1">
                        {event.company_name || event.title}
                      </div>

                      {/* Statut */}
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs text-gray-800 font-medium truncate">
                          {event.status}
                        </span>
                      </div>

                      {/* Centre */}
                      <div className="text-xs text-gray-700 truncate">
                        {event.center}
                      </div>

                      {/* Heure */}
                      {event.event_date && (
                        <div className="text-xs text-gray-600 mt-1">
                          {formatParisTime(event.event_date, {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Drawer */}
      {selectedCase && (
        <CaseDrawer
          case={selectedCase}
          onClose={() => setSelectedCase(null)}
        />
      )}
    </div>
  );
}
